/**
 * ProseMirror schema for a screenplay: a flat list of typed paragraph blocks.
 * doc → para+ ; para → inline text with marks. Run-level attributes that Fade
 * In carries (font, and unknown attrs like bgcolor) are modeled as marks so
 * they survive editing and serialize back verbatim.
 */
import { Schema } from "prosemirror-model";

export const screenplaySchema = new Schema({
  nodes: {
    doc: { content: "para+" },

    para: {
      content: "inline*",
      group: "block",
      attrs: {
        styleName: { default: "Action" },
        align: { default: null },
        bookmark: { default: null },
        // Preserved but unmodeled <para>/<style> attributes (round-trip).
        extra: { default: null },
        styleExtra: { default: null },
        // Original runs of an empty paragraph, so attribute residue on an empty
        // <text> (e.g. font="Courier New") survives a model→PM→model round-trip.
        blankRuns: { default: null },
      },
      // Not directly parsed from clipboard HTML; content is built via convert.ts.
      parseDOM: [{ tag: "div.para" }],
      toDOM(node) {
        const attrs: Record<string, string> = { class: "para" };
        attrs["data-style"] = String(node.attrs.styleName);
        if (node.attrs.align) attrs["data-align"] = String(node.attrs.align);
        return ["div", attrs, 0];
      },
    },

    text: { group: "inline" },
  },

  marks: {
    // Order matters only for serialization determinism; content is unaffected.
    strong: {
      parseDOM: [{ tag: "strong" }, { tag: "b" }],
      toDOM() {
        return ["strong", 0];
      },
    },
    em: {
      parseDOM: [{ tag: "em" }, { tag: "i" }],
      toDOM() {
        return ["em", 0];
      },
    },
    underline: {
      parseDOM: [{ tag: "u" }, { style: "text-decoration=underline" }],
      toDOM() {
        return ["u", 0];
      },
    },
    /** Preserves a run's font attribute (e.g. pasted "Courier New"). */
    font: {
      attrs: { family: { default: "" } },
      parseDOM: [{ tag: "span[data-font]", getAttrs: (el) => ({ family: (el as HTMLElement).getAttribute("data-font") ?? "" }) }],
      toDOM(mark) {
        return ["span", { "data-font": String(mark.attrs.family) }, 0];
      },
    },
    /** Preserves any unmodeled <text> attributes as JSON (e.g. bgcolor). */
    runExtra: {
      attrs: { json: { default: "{}" } },
      parseDOM: [{ tag: "span[data-run-extra]", getAttrs: (el) => ({ json: (el as HTMLElement).getAttribute("data-run-extra") ?? "{}" }) }],
      toDOM(mark) {
        return ["span", { "data-run-extra": String(mark.attrs.json) }, 0];
      },
    },
  },
});

export type ScreenplaySchema = typeof screenplaySchema;
